import { AxiosError } from 'axios'
import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'

import { AccessPrograms } from './AccessPrograms'
import {
  programColumns,
  defaultRowsPerPage,
  defaultSearchParams,
  defaultPagination
} from './AccessProgramsContainer.models'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import {
  getParamValueFor,
  IPagination,
  useUpdateQueryParam
} from '../../components/ClinPagination/ClinPagination.model'
import { SortDirectionType } from '../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { UserRoleRecord, UserRole, ProgramColumnNames } from '../../constants'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  getProgramsSummary,
  cancelGetProgramsSummary,
  AuthError
} from '../../services/ApiService'
import { IProgramSummary } from '../../types'
import { ProgramSummarySearchDto } from '../../types/swaggerTypes'
import useChangeBackgroundColor from '../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { ClinTheme } from '../../ClinTheme'

interface IAccessProgramsContainerProps extends RouteComponentProps {}

export const AccessProgramsContainer: React.FC<
  IAccessProgramsContainerProps
> = ({ history, location }) => {
  const {
    institute,
    userRole,
    supportContact,
    dispatch,
    portfolioCountryCode
  } = useAppContext()
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [programs, setPrograms] = React.useState<IProgramSummary[]>([])
  const isMaUser = userRole && UserRoleRecord[userRole as UserRole].isMaUser

  // Restore page state from URL params
  const urlSearchParams = new URLSearchParams(location.search)

  // Restore pagination state
  // PageSize
  if (defaultSearchParams.pagination) {
    defaultSearchParams.pagination.take = getParamValueFor(
      'pageSize',
      urlSearchParams,
      defaultRowsPerPage
    )
    // PageIndex
    defaultPagination.take = defaultSearchParams.pagination.take
    defaultSearchParams.pagination.skip =
      defaultSearchParams.pagination.take *
      getParamValueFor('pageIndex', urlSearchParams)
  }

  const [pagination, setPagination] =
    React.useState<IPagination>(defaultPagination)

  const [searchParams, setSearchParams] =
    React.useState<ProgramSummarySearchDto>(defaultSearchParams)
  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState<
    string[]
  >([])
  const [therapeuticAreaOptions, setTherapeuticAreaOptions] = React.useState<
    Array<{ label: string; value: string }>
  >([])
  const [selectedTherapeuticAreas, setSelectedTherapeuticAreas] =
    React.useState<Array<{ label: string; value: string }>>([])
  const [isTherapeuticAreasExpanded, setIsTherapeuticAreasExpanded] =
    React.useState<boolean>(false)

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  const handlePageClicked = (selectedPageIndex: number) => {
    cancelGetProgramsSummary()
    setIsLoading(true)
    const take = searchParams.pagination?.take ?? defaultRowsPerPage
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: (selectedPageIndex - 1) * take,
        take: take
      }
    })
  }

  const handleRowClicked = (programId: number) => {
    cancelGetProgramsSummary()
    history.push(`/programs/access-programs/${programId}`, {
      from: window.location.pathname
    })
  }

  const handleOnSearch = (query: string) => {
    cancelGetProgramsSummary()
    setIsLoading(true)
    const take = searchParams.pagination?.take ?? defaultRowsPerPage
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: take
      },
      query
    })
  }

  const handleSetSelectedFilterOption = (filterKey: string) => {
    cancelGetProgramsSummary()
    setIsLoading(true)

    // Toggle filter selection
    const isCurrentlySelected = selectedFilterOptions.includes(filterKey)
    const newSelectedFilters = isCurrentlySelected
      ? selectedFilterOptions.filter((f) => f !== filterKey)
      : [...selectedFilterOptions, filterKey]

    setSelectedFilterOptions(newSelectedFilters)

    // Update search params with filter values
    const newFilter = { ...searchParams.filter }

    // Map filter keys to API values ('Y' or undefined)
    if (filterKey === 'enrolled') {
      newFilter.enrolled = isCurrentlySelected ? undefined : 'Y'
    } else if (filterKey === 'acceptingNewPatients') {
      newFilter.acceptingNewPatients = isCurrentlySelected ? undefined : 'Y'
    } else if (filterKey === 'resupplyOnly') {
      newFilter.resupplyOnly = isCurrentlySelected ? undefined : 'Y'
    }

    const take = searchParams.pagination?.take ?? defaultRowsPerPage
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: take
      },
      filter: newFilter
    })
  }

  const handleTherapeuticAreasChange = (
    selectedOptions: Array<{ label: string; value: string }>
  ) => {
    cancelGetProgramsSummary()
    setIsLoading(true)
    setSelectedTherapeuticAreas(selectedOptions)

    const take = searchParams.pagination?.take ?? defaultRowsPerPage
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: take
      },
      filter: {
        ...searchParams.filter,
        therapeuticAreas: selectedOptions.map((option) => ({
          name: option.value
        }))
      }
    })
  }

  const handleToggleTherapeuticAreasExpanded = () => {
    setIsTherapeuticAreasExpanded(!isTherapeuticAreasExpanded)
  }

  const handleTherapeuticAreaChipClick = (therapeuticArea: string) => {
    const isCurrentlySelected = selectedTherapeuticAreas.some(
      (ta) => ta.value === therapeuticArea
    )
    const newSelectedAreas = isCurrentlySelected
      ? selectedTherapeuticAreas.filter((ta) => ta.value !== therapeuticArea)
      : [
          ...selectedTherapeuticAreas,
          { label: therapeuticArea, value: therapeuticArea }
        ]

    handleTherapeuticAreasChange(newSelectedAreas)
  }

  useEffect(() => {
    // handle case when user sort table by "Availability" and then switching to the first tab
    if (searchParams.sorting?.sortBy === ProgramColumnNames.Availability) {
      setSearchParams({
        ...searchParams,
        sorting: {
          sortBy: programColumns[1].name,
          order: SortDirectionType.Ascending.toUpperCase()
        }
      })
      return
    }

    institute?.data &&
      !institute.isLoading &&
      getProgramsSummary(searchParams)
        .then((response) => {
          setIsLoading(false)
          if (response) {
            const programsFromAPI: IProgramSummary[] | null =
              response.data.result
            setPrograms(programsFromAPI)
            const paginationDto = response.data.pagination
            paginationDto &&
              setPagination({
                count: paginationDto.count,
                skip: paginationDto.skip,
                take: paginationDto.take,
                total: paginationDto.total
              })

            // Extract therapeutic area options from facets
            const facets = response.data.facets
            if (facets) {
              const therapeuticAreaFacet = facets.find(
                (facet) => facet.field === 'Therapeutic Area'
              )
              if (therapeuticAreaFacet) {
                const options = therapeuticAreaFacet.values.map((v) => ({
                  label: v.value,
                  value: v.value
                }))
                setTherapeuticAreaOptions(options)
              }
            }
          }
        })
        .catch((error: AxiosError) => {
          const { code, message } = error

          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }

          // If it's a 400 error with no results, treat it as an empty result set
          if (error.response && error.response.status === 400) {
            setPrograms([])
            setPagination({
              count: 0,
              skip: 0,
              take: searchParams.pagination?.take ?? defaultRowsPerPage,
              total: 0
            })
            setIsLoading(false)
            return
          }

          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error fetching access programs. ${title ?? ''} ${
                  message ?? ''
                }`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error fetching access programs. ${message} ${
                  code ?? ''
                }`
              )
            )
          }

          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
          setIsLoading(false)
        })
    return () => {
      cancelGetProgramsSummary()
    }
  }, [searchParams, dispatch, institute.data, institute.isLoading, institute])

  const handlePageSizeChange = (pageSize: number) => {
    cancelGetProgramsSummary()
    setIsLoading(true)
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: pageSize
      }
    })
  }
  // Track changes to pagination and tabs as they update and update query string if it changes
  const skip = searchParams.pagination?.skip ?? 0
  const take = searchParams.pagination?.take ?? defaultRowsPerPage
  useUpdateQueryParam({
    pageIndex: skip / take
  })
  useUpdateQueryParam({ pageSize: take })

  type Deps = boolean[]
  useEffectOnlyOnce(
    () => {
      analyticsServiceSingleton.trackPageView(
        AnalyticsPageEvent.ViewProgramCatalogue,
        {}
      )
    },
    [isLoading],
    (dependencies: Deps) => dependencies[0] === false
  )

  return (
    <AccessPrograms
      isLoading={isLoading}
      programs={programs}
      pagination={pagination}
      isMaUser={isMaUser}
      supportContact={supportContact}
      userCountry={portfolioCountryCode}
      selectedFilterOptions={selectedFilterOptions}
      initialQuery={searchParams.query || ''}
      therapeuticAreaOptions={therapeuticAreaOptions}
      selectedTherapeuticAreas={selectedTherapeuticAreas}
      isTherapeuticAreasExpanded={isTherapeuticAreasExpanded}
      handlePageClicked={handlePageClicked}
      handleRowClicked={handleRowClicked}
      handlePageSizeChange={handlePageSizeChange}
      handleOnSearch={handleOnSearch}
      handleSetSelectedFilterOption={handleSetSelectedFilterOption}
      handleTherapeuticAreasChange={handleTherapeuticAreasChange}
      handleToggleTherapeuticAreasExpanded={
        handleToggleTherapeuticAreasExpanded
      }
      handleTherapeuticAreaChipClick={handleTherapeuticAreaChipClick}
    />
  )
}
